/**
 * Object → «Налаштування» (iteration 2): parameters of the object (the outage
 * reserve is set here only), CT at the input, region and DSO, tariff with
 * zone rates — saved by the bar of unsaved changes; the inverter; the
 * history; the original inverter settings saved when the access opened and
 * (D2) the inverter's schedule now with «Оновити з інвертора»; (D3)
 * «Доступ Phage.Energy» with «Перевірити доступ» and how to open or close it;
 * «Відключити об'єкт» with a confirmation, then (D4) the hint to close the
 * access in DeyeCloud. D4: no DeyeCloud password is kept — nothing to update.
 */
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { InputCt, SiteResponse, SiteSettingsBody, TariffType } from 'shared';

import { AdminBanner } from '../../components/AdminBanner.js';
import { AppShell, homePath } from '../../components/AppShell.js';
import { InverterLimits } from '../../components/InverterLimits.js';
import { InverterProgram } from '../../components/InverterProgram.js';
import {
  CtField,
  RegionFields,
  TariffFields,
  defaultRateTexts,
  parseRates,
} from '../../components/ObjectFields.js';
import { PhageAccessMore, PhageAccessRow } from '../../components/PhageAccess.js';
import { ProgramChangedWarning } from '../../components/ProgramChangedWarning.js';
import { useToast } from '../../components/Toast.js';
import { ZeroCurrentWarning } from '../../components/ZeroCurrentWarning.js';
import { apiCode, errorText, notFound } from '../../components/errors.js';
import { fmtDateTime, fmtNumShort, modelLabel, parseNum, serialOf } from '../../components/format.js';
import { workModeText } from '../../components/inverter-program.js';
import { regionOf } from '../../components/regions.js';
import { HISTORY_DAYS, histState } from '../../components/site-state.js';
import {
  type RatedTariff,
  ZONES,
  type ZoneName,
  hasRates,
  isZone,
  ratesFromCells,
  zoneCells,
} from '../../components/tariff.js';
import { useProfile } from '../../hooks/use-profile.js';
import { useSiteAnalysis, useSiteOverview, useSitePotential } from '../../hooks/use-site-results.js';
import { useDisconnectSite, usePhageAccessInfo, useSite, useUpdateSettings } from '../../hooks/use-sites.js';
import { useSaveTariff } from '../../hooks/use-tariff.js';
import { useLocale } from '../../i18n/locale-context.js';
import type { MessageKey } from '../../i18n/messages.js';
import { NotFoundCard } from './NotFoundPage.js';
import { SiteHeader } from './SiteHeader.js';

interface Form {
  name: string;
  region: string;
  osr: string;
  cap: string;
  pmax: string;
  allowed: string;
  reserve: string;
  maxReserve: boolean;
  ct: InputCt;
  socMin: string;
  socMax: string;
  tariff: TariffType | '';
  rates: Partial<Record<ZoneName, string>>;
}

function formOf(
  site: SiteResponse,
  rates: Partial<Record<ZoneName, number>> | null,
  locale: 'uk' | 'en',
): Form {
  const num = (v: number | null) => (v === null ? '' : fmtNumShort(locale, v, 2));
  // A region no longer in the list («Інша область») is not chosen: «Оберіть область».
  const region = regionOf(site.region) ? (site.region ?? '') : '';
  return {
    name: site.name,
    region,
    osr: region ? (site.osr_code ?? '') : '',
    cap: num(site.capacity_kwh),
    pmax: num(site.p_max_kw),
    allowed: num(site.allowed_power_kw),
    reserve: num(site.reserve_pct),
    maxReserve: site.max_reserve,
    ct: site.input_ct,
    socMin: num(site.soc_min_pct),
    socMax: num(site.soc_max_pct),
    tariff: site.tariff_type ?? '',
    rates: Object.fromEntries(
      Object.entries(rates ?? {}).map(([k, v]) => [k, fmtNumShort(locale, v ?? 0, 2)]),
    ),
  };
}

function OriginalSettings({ site }: { site: SiteResponse }) {
  const { t, tf } = useLocale();
  const orig = site.original_settings;
  // Owner, 23.10.2026: not stored (refused, or lost) — nothing is written.
  if (!orig && site.inverter_settings.unreadable) {
    return <p className="hint">{t('site.arb.no_original')}</p>;
  }
  if (!orig) return <p className="sub">{t('site.orig.none')}</p>;
  const mode = (orig.system as { systemWorkMode?: unknown } | null)?.systemWorkMode;
  const tou = (orig.tou as { touAction?: unknown } | null)?.touAction;
  const touText =
    tou === 'on' ? t('site.orig.tou.on') : tou === 'off' ? t('site.orig.tou.off') : t('site.orig.unknown');
  return (
    <>
      <p className="sub" style={{ margin: '4px 0 10px' }}>
        {tf('site.orig.saved', { at: fmtDateTime(orig.saved_at, site.timezone) })}
      </p>
      <dl className="kv2">
        <dt>{t('site.orig.mode')}</dt>
        <dd>{workModeText(t, mode)}</dd>
        <dt>{t('site.orig.tou')}</dt>
        <dd>{touText}</dd>
      </dl>
    </>
  );
}

function Disconnect({ site, home, company }: { site: SiteResponse; home: string; company: string }) {
  const { t, tf } = useLocale();
  const navigate = useNavigate();
  const toast = useToast();
  const off = useDisconnectSite(site.id);
  const [asking, setAsking] = useState(false);
  const failed = apiCode(off.error) === 'restore_failed';
  const run = (force: boolean) =>
    off.mutate(force, {
      onSuccess: (r) => {
        const key: MessageKey =
          r.restore.status === 'sent'
            ? 'site.off.done'
            : r.restore.reason === 'access_not_open' || r.restore.reason === 'no_edit_right'
              ? 'site.off.done_no_access'
              : 'site.off.done_plain';
        navigate(home, { replace: true });
        // D4: the client closes the access in DeyeCloud themselves.
        toast.show(`${tf(key, { name: site.name })} ${tf('site.off.close_access', { company })}`);
      },
    });
  return (
    <section className="card danger-zone" aria-labelledby="off-title">
      <div className="card-head">
        <h2 id="off-title">{t('site.off.title')}</h2>
      </div>
      <p className="sub" style={{ margin: 0, maxWidth: '72ch' }}>
        {tf('site.off.text', { name: company })}
      </p>
      <div className="actions">
        {!asking ? (
          <button type="button" className="btn danger" onClick={() => setAsking(true)}>
            {t('site.off.ask')}
          </button>
        ) : (
          <>
            <b style={{ width: '100%' }}>{tf('site.off.confirm', { name: site.name })}</b>
            <button
              type="button"
              className="btn danger solid"
              onClick={() => run(false)}
              disabled={off.isPending}
            >
              {t('site.off.yes')}
            </button>
            <button type="button" className="btn" onClick={() => setAsking(false)} disabled={off.isPending}>
              {t('common.cancel')}
            </button>
          </>
        )}
      </div>
      {failed ? (
        <div className="actions">
          <p className="err" role="alert" style={{ width: '100%' }}>
            {t('site.off.failed')}
          </p>
          <button type="button" className="btn danger" onClick={() => run(true)} disabled={off.isPending}>
            {t('site.off.force')}
          </button>
        </div>
      ) : off.error ? (
        <p className="err" role="alert" style={{ marginTop: 10 }}>
          {errorText(off.error, tf)}
        </p>
      ) : null}
    </section>
  );
}

export function SiteSettingsPage() {
  const { id = '' } = useParams();
  const { t, tf, tp, locale } = useLocale();
  const toast = useToast();
  const me = useProfile();
  const site = useSite(id);
  const overview = useSiteOverview(id);
  const analysis = useSiteAnalysis(id);
  const potential = useSitePotential(id, false);
  const update = useUpdateSettings(id);
  const saveTariff = useSaveTariff(id);
  const accessInfo = usePhageAccessInfo();

  const zoneNow = hasRates(site.data?.tariff_type) ? (site.data?.tariff_type as RatedTariff) : null;
  const storedCells = site.data?.zone_rates_per_kwh ?? null;
  const knownRates = useMemo(
    () => (zoneNow && storedCells ? ratesFromCells(zoneNow, storedCells) : null),
    [zoneNow, storedCells],
  );
  const initial = useMemo(
    () => (site.data ? formOf(site.data, knownRates, locale) : null),
    [site.data, knownRates, locale],
  );
  const [form, setForm] = useState<Form | null>(null);
  const [checked, setChecked] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // The form follows the stored object (a refetch, original settings arriving,
  // a save) until the user edits it; edits survive a refetch.
  const edited = useRef(false);
  useEffect(() => {
    if (initial && !edited.current) setForm(initial);
  }, [initial]);

  if (notFound(site.error)) {
    return (
      <AppShell>
        <div className="view">
          <NotFoundCard />
        </div>
      </AppShell>
    );
  }
  if (!site.data || !form || !initial) {
    return (
      <AppShell>
        <p className={site.isError ? 'hist-note err' : 'loading'}>
          {t(site.isError ? 'common.load_error' : 'common.loading')}
        </p>
      </AppShell>
    );
  }

  const s = site.data;
  /** D6: «з DeyeCloud» / «вручну» next to a parameter; null — not set. */
  const src = (field: string) =>
    s.params_source[field] ? (
      <small className="sub">
        {t(s.params_source[field] === 'deye' ? 'add.src.deye' : 'add.src.manual')}
      </small>
    ) : null;
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = (patch: Partial<Form>) => {
    edited.current = true;
    setSaveError(null);
    setForm({ ...form, ...patch });
  };
  const zone = hasRates(form.tariff || null) ? (form.tariff as RatedTariff) : null;
  // Stored cells that do not follow the zones (entered another way) still apply;
  // for a fixed tariff they are cells of a zone tariff before — not its rate.
  const unread = isZone(zoneNow) && !!storedCells && !knownRates && form.tariff === zoneNow;

  const values = {
    cap: parseNum(form.cap),
    pmax: parseNum(form.pmax),
    allowed: form.allowed.trim() ? parseNum(form.allowed) : null,
    reserve: parseNum(form.reserve),
    socMin: parseNum(form.socMin),
    socMax: parseNum(form.socMax),
  };
  const ratesTouched = zone
    ? ZONES[zone].some((z) => (form.rates[z] ?? '') !== (initial.rates[z] ?? ''))
    : false;
  const needRates = !!zone && (form.tariff !== initial.tariff || ratesTouched);
  const rates = zone && needRates ? parseRates(zone, form.rates) : null;
  const errors: Partial<Record<string, MessageKey>> = {};
  if (!form.name.trim()) errors.name = 'site.st.err.name';
  if (!(values.cap > 0)) errors.cap = 'site.st.err.cap';
  if (!(values.pmax > 0)) errors.pmax = 'site.st.err.pmax';
  if (values.allowed !== null && !(values.allowed > 0)) errors.allowed = 'site.st.err.allowed';
  if (!(values.reserve >= 0 && values.reserve <= 90)) errors.reserve = 'site.st.err.reserve';
  if (!(values.socMin >= 0 && values.socMax <= 100 && values.socMin < values.socMax))
    errors.soc = 'site.st.err.soc';
  if (needRates && !rates) errors.rates = 'site.st.err.rate';
  const err = (k: string) =>
    checked && errors[k] ? <small className="err">{t(errors[k] as MessageKey)}</small> : null;

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setChecked(true);
    if (Object.keys(errors).length > 0) return;
    const body: SiteSettingsBody = {};
    if (form.name.trim() !== initial.name) body.name = form.name.trim();
    if (form.cap !== initial.cap) body.capacity_kwh = values.cap;
    if (form.pmax !== initial.pmax) body.p_max_kw = values.pmax;
    if (form.allowed !== initial.allowed) body.allowed_power_kw = values.allowed;
    if (form.reserve !== initial.reserve) body.reserve_pct = values.reserve;
    if (form.maxReserve !== initial.maxReserve) body.max_reserve = form.maxReserve;
    if (form.socMin !== initial.socMin) body.soc_min_pct = values.socMin;
    if (form.socMax !== initial.socMax) body.soc_max_pct = values.socMax;
    if (form.ct !== initial.ct) body.input_ct = form.ct;
    if (form.region !== initial.region && form.region) body.region = form.region;
    if (form.osr !== initial.osr) body.osr_code = form.osr || null;
    if (form.tariff !== initial.tariff && form.tariff) body.tariff_type = form.tariff;
    // The saved values come back as the stored object: the form follows it.
    edited.current = false;
    try {
      const saveRates = async () => {
        if (zone && rates) await saveTariff.mutateAsync(zoneCells(zone, rates));
      };
      const saveBody = async () => {
        if (Object.keys(body).length > 0) await update.mutateAsync(body);
      };
      // Zone rates first: a plan by a zone tariff reads them. A fixed tariff:
      // the type first — its rate put while the object is still zonal would
      // re-plan it by the flat rate and could reach the inverter.
      if (form.tariff === 'fixed') {
        await saveBody();
        await saveRates();
      } else {
        await saveRates();
        await saveBody();
      }
      setChecked(false);
      toast.show(t('site.st.saved'));
    } catch (error) {
      edited.current = true;
      setSaveError(tf('site.st.save_err', { msg: errorText(error, tf) }));
    }
  };

  const hist = histState(analysis.data, potential.data?.status === 'ok');
  // D4: the download starts once Phage.Energy has access to the station.
  const histText =
    s.phage_access.status === 'pending'
      ? t('site.inv.hist.no_access')
      : !hist
        ? ''
        : hist.kind === 'done'
          ? tf('site.inv.hist.done', { days: HISTORY_DAYS })
          : hist.kind === 'few'
            ? tp('site.hist.few', hist.days)
            : hist.kind === 'loading'
              ? tf('site.inv.hist.loading', { done: hist.done, total: HISTORY_DAYS })
              : hist.kind === 'pending'
                ? t('site.inv.hist.pending')
                : t('site.inv.hist.failed');
  return (
    <AppShell>
      <div className="view">
        <AdminBanner siteId={id} />
        <SiteHeader site={s} latest={overview.data?.latest} hist={hist} tab="settings" />
        <form noValidate onSubmit={onSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <section className="card">
            <div className="group">
              <h3 className="group-title">{t('site.st.object')}</h3>
              <div className="form-grid">
                <label className="field full">
                  <span>{t('site.st.name')}</span>
                  <input
                    value={form.name}
                    autoComplete="off"
                    onChange={(e) => set({ name: e.target.value })}
                  />
                  {err('name') ?? src('name')}
                </label>
                <RegionFields
                  region={form.region}
                  osr={form.osr}
                  onChange={(region, osr) => set({ region, osr })}
                />
                {src('region')}
              </div>
            </div>
          </section>
          <section className="card">
            <div className="group">
              <h3 className="group-title">{t('site.st.battery')}</h3>
              <div className="form-grid">
                <label className="field">
                  <span>{t('site.st.cap')}</span>
                  <input
                    inputMode="decimal"
                    value={form.cap}
                    onChange={(e) => set({ cap: e.target.value })}
                  />
                  {err('cap') ?? src('capacity_kwh') ?? <small>{t('site.st.cap_hint')}</small>}
                </label>
                <label className="field">
                  <span>{t('site.st.pmax')}</span>
                  <input
                    inputMode="decimal"
                    value={form.pmax}
                    onChange={(e) => set({ pmax: e.target.value })}
                  />
                  {err('pmax') ?? src('p_max_kw')}
                </label>
                <label className="field">
                  <span>{t('site.st.allowed')}</span>
                  <input
                    inputMode="decimal"
                    value={form.allowed}
                    onChange={(e) => set({ allowed: e.target.value })}
                  />
                  {err('allowed') ?? <small>{t('site.st.allowed_hint')}</small>}
                </label>
                <label className="field">
                  <span>{t('site.st.reserve')}</span>
                  <input
                    inputMode="numeric"
                    value={form.reserve}
                    onChange={(e) => set({ reserve: e.target.value })}
                  />
                  {err('reserve') ?? <small>{t('site.st.reserve_hint')}</small>}
                </label>
                <div className="field">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={form.maxReserve}
                      onChange={(e) => set({ maxReserve: e.target.checked })}
                    />
                    <span>{t('site.st.max_reserve')}</span>
                  </label>
                  <small>{tf('site.st.max_reserve_hint', { max: form.socMax })}</small>
                </div>
                <CtField name="s-ct" value={form.ct} onChange={(ct) => set({ ct })} />
                {src('input_ct')}
              </div>
              <details className="more" open={!!errors.soc && checked}>
                <summary>{t('site.st.limits')}</summary>
                <div className="form-grid">
                  <label className="field">
                    <span>{t('site.st.socmin')}</span>
                    <input
                      inputMode="numeric"
                      value={form.socMin}
                      onChange={(e) => set({ socMin: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span>{t('site.st.socmax')}</span>
                    <input
                      inputMode="numeric"
                      value={form.socMax}
                      onChange={(e) => set({ socMax: e.target.value })}
                    />
                  </label>
                  {checked && errors.soc ? <small className="err full">{t(errors.soc)}</small> : null}
                </div>
              </details>
            </div>
          </section>
          <section className="card">
            <TariffFields
              name="s-tariff"
              tariff={form.tariff}
              rates={form.rates}
              unread={unread}
              onChange={(tariff, r) => {
                const next = hasRates(tariff) ? (tariff as RatedTariff) : null;
                const empty = next ? ZONES[next].every((z) => !r[z]) : false;
                set({
                  tariff,
                  rates: next && empty && tariff !== zoneNow ? defaultRateTexts(next, locale) : r,
                });
              }}
            />
            {err('rates')}
          </section>
          {dirty ? (
            <div className="savebar">
              <span>{t('site.st.dirty')}</span>
              <span className="savebar-buttons">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    edited.current = false;
                    setForm(initial);
                    setChecked(false);
                    setSaveError(null);
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={update.isPending || saveTariff.isPending}
                >
                  {t('common.save')}
                </button>
              </span>
            </div>
          ) : null}
          {saveError ? (
            <p className="err" role="alert">
              {saveError}
            </p>
          ) : null}
        </form>

        <section className="card" aria-labelledby="inv-title">
          <div className="card-head">
            <h2 id="inv-title">{t('site.inv.title')}</h2>
          </div>
          <dl className="kv2">
            <dt>{t('site.inv.sn')}</dt>
            <dd className="num">{serialOf(s.vendor_site_id)}</dd>
            <dt>{t('site.inv.model')}</dt>
            <dd>{modelLabel(s.model) ?? 'Deye'}</dd>
            <dt>{t('access.row')}</dt>
            <dd>
              <PhageAccessRow site={s} />
            </dd>
            <dt>{t('site.inv.history')}</dt>
            <dd>{histText}</dd>
          </dl>
          <PhageAccessMore name={accessInfo.data?.company_name ?? null} />
          <h3 className="group-title" style={{ marginTop: 20 }}>
            {t('site.orig.title')}
          </h3>
          <OriginalSettings site={s} />
          <ProgramChangedWarning site={s} />
          <ZeroCurrentWarning site={s} />
          <InverterLimits site={s} />
          <InverterProgram
            site={s}
            canRefresh={s.phage_access.status === 'open' || s.phage_access.status === 'noedit'}
          />
        </section>
        <Disconnect
          site={s}
          home={homePath(me.data?.role)}
          company={accessInfo.data?.company_name ?? 'Phage.Energy'}
        />
      </div>
    </AppShell>
  );
}
